






























































import { defineComponent, computed, PropType, ref, onMounted } from '@vue/composition-api'
import CommonModalSection from '@/components/common/modal/CommonModalSection.vue'
import AtomButton from '@/components/atoms/AtomButton.vue'
import i18n from '@/locales/I18n'
import type { RewardDataType } from '@/store/hook/videoPlatformContents/useRewards'
import CloudFrontUtil from '@/util/aws/CloudFrontUtil'
import LimitedVideoModalSection from '@/components/HomePage/HomePane/LimitedVideoModalSection.vue'
import useDownload from '@/util/download/useDownload'
import InAppYoutubeBrowser from '@/util/inAppBrowser/InAppYoutubeBrowser'
import InAppBrowserWrapper, { InAppBrowserResult } from '@/util/inAppBrowser/InAppBrowserWrapper'
import MessageDialogStore from '@/store/stores/pageStore/common/MessageDialogStore'
import DeviceInfo from '@/util/DeviceInfo'
import FileExtensionUtil from '@/util/FileExtensionUtil'

export default defineComponent({
  name: 'RewardNoticeModalSection',
  components: { LimitedVideoModalSection, AtomButton, CommonModalSection },
  props: {
    reward: {
      type: Object as PropType<RewardDataType>,
      required: true,
    },
    newArrivalsRewardsLength: {
      type: Number,
      default: 0,
    },
  },
  setup(props, { emit }) {
    const { loadFileToDownload, loadFileToDownloadForCordova } = useDownload()

    // YouTubeをInAppBrowserで開く
    const inAppBrowser = new InAppYoutubeBrowser()

    // YouTubeモーダルを開くかどうか
    const videoModalEnabled = ref(false)

    const imageStyle = ref({})
    const rewardImage = ref<HTMLImageElement | null>(null)

    // 特典動画かどうか
    const isVideo = computed(
      () =>
        props.reward.videoType.startsWith('rewardVideo') ||
        props.reward.videoType === 'userSpecifiedRewardVideo',
    )

    const buttonLabel = computed(() =>
      isVideo.value
        ? i18n.tc('RewardPage.buttonLabel.video')
        : i18n.tc('RewardPage.buttonLabel.image'),
    )

    const deadlineLabel = computed(() =>
      isVideo.value ? i18n.tc('RewardPage.deadline.video') : i18n.tc('RewardPage.deadline.image'),
    )

    const signedImagePath = computed(() => CloudFrontUtil.getSignedUrl(props.reward.imagePath))

    /**
     * YouTubeを再生する
     */
    const playYouTube = () => {
      if (InAppBrowserWrapper.isAvailable()) {
        // アプリで動作している場合、InAppBrowserを利用してYoutube動画を再生する
        inAppBrowser.showYoutube(props.reward.videoId, async (result: InAppBrowserResult) => {
          if (result?.isNetworkError) {
            await MessageDialogStore.value.open({
              title: i18n.t('apiNotification.dialog.0.title') as string,
              message: i18n.t('apiNotification.dialog.0.message') as string,
            })
          } else if (result?.isError) {
            await MessageDialogStore.value.open({
              title: i18n.t('RewardPage.errors.VideoPlayError.title') as string,
              message: i18n.t('RewardPage.errors.VideoPlayError.message') as string,
            })
          }
        })
      } else {
        videoModalEnabled.value = true
      }
    }

    /**
     * 動画モーダルを閉じる
     */
    const hideVideoModal = () => {
      videoModalEnabled.value = false
    }

    /**
     * 画像をダウンロードする
     */
    const downloadImage = async () => {
      if (DeviceInfo.isCordova()) {
        const downloadResult = await loadFileToDownloadForCordova(signedImagePath.value)
        if (downloadResult?.saveImage?.isSuccess === false) {
          if (downloadResult.saveImage.response.errorCode === 'vp_110_008') {
            MessageDialogStore.value.open({
              title: i18n.tc('RewardPage.errors.downloadImagePermissionError.title'),
              message: i18n.tc('RewardPage.errors.downloadImagePermissionError.message'),
            })
          } else {
            MessageDialogStore.value.open({
              title: i18n.tc('RewardPage.errors.downloadImageError.title'),
              message: i18n.tc('RewardPage.errors.downloadImageError.message'),
            })
          }
        } else if (downloadResult?.loadFile && downloadResult?.loadFile?.status !== 200) {
          MessageDialogStore.value.open({
            title: i18n.tc('RewardPage.errors.loadImageError.title'),
            message: i18n.tc('RewardPage.errors.loadImageError.message'),
          })
        } else {
          MessageDialogStore.value.open({
            title: i18n.tc('RewardPage.success.downloadImage.title'),
            message: i18n.tc('RewardPage.success.downloadImage.message'),
          })
        }
      } else {
        // 画像パスを見て、拡張子をつける
        const extension = FileExtensionUtil.getFileExtension(props.reward.imagePath)
        const downloadResult = await loadFileToDownload(
          `SFgo_${props.reward.title.en}.${extension}`,
          signedImagePath.value,
          {
            responseType: 'blob',
            headers: {
              accept: 'image/*',
            },
          },
        )
        if (!downloadResult) {
          MessageDialogStore.value.open({
            title: i18n.tc('RewardPage.errors.loadImageError.title'),
            message: i18n.tc('RewardPage.errors.loadImageError.message'),
          })
        } else {
          MessageDialogStore.value.open({
            title: i18n.tc('RewardPage.success.downloadImage.title'),
            message: i18n.tc('RewardPage.success.downloadImage.message'),
          })
        }
      }
    }

    const checkImageAspect = () => {
      if (!rewardImage.value) {
        return
      }

      rewardImage.value.onload = () => {
        const imageWidth = rewardImage.value?.getBoundingClientRect().width || 0
        const imageHeight = rewardImage.value?.getBoundingClientRect().height || 0

        if (imageWidth < imageHeight) {
          imageStyle.value = { height: '274px' }
        }
      }
    }

    const handleClick = () => {
      if (isVideo.value) {
        playYouTube()
      } else {
        downloadImage()
      }
    }

    const handleShowListPageClicked = () => {
      emit('onClickShowRewardPage')
    }

    const handleCloseClicked = () => {
      emit('onClickClose')
    }

    onMounted(() => {
      checkImageAspect()
    })

    return {
      rewardImage,
      imageStyle,
      buttonLabel,
      deadlineLabel,
      signedImagePath,
      videoModalEnabled,
      handleShowListPageClicked,
      handleCloseClicked,
      handleClick,
      hideVideoModal,
    }
  },
})
